import React from 'react';
import { Table, Badge, Button, EmptyState, Card } from '@parsina/framework';
import { Stethoscope } from 'lucide-react';

import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import { encounterStatusKey } from '../../../constants/vocabulary.js';
import { visitsNewestFirst, firstLine } from '../PatientRecord.shared.js';

// ویزیت‌ها — every visit this patient has had, newest first. core.md §7.4.
//
// `seq` orders them and `seq` NEVER REUSES A NUMBER (core.md §6, rule 6): a
// cancelled visit 3 followed by a new one gives «ویزیت ۴», and the list simply
// does not show a 3. Nothing here implies a gap, because there is no gap to
// imply — the visit was cancelled, not lost.
//
// A row opens the workspace. An OPEN visit opens it to be written in; a CLOSED
// one opens it read-only with «بازگشایی» — the same route either way, because
// "which visit am I looking at" and "may I type in it" are different questions
// and only the second one has a state.
//
// The desktop renders a table and the phone a stack of cards, from ONE row
// model: `columns` below is the reading order for both.

export default function VisitsTab({ encounters, onOpenVisit, onStartVisit, canStart, mobile }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const rows = React.useMemo(() => visitsNewestFirst(encounters), [encounters]);

  const counts = (row) => [
    formatNumber(Number(row.diagnosisCount || 0)),
    formatNumber(Number(row.rxCount || 0)),
    formatNumber(Number(row.orderCount || 0)),
  ].join(' · ');

  if (!rows.length) {
    return (
      <EmptyState
        icon={<Stethoscope size={44} strokeWidth={1.5} aria-hidden />}
        title={t('record.visits.empty.title')}
        description={t('record.visits.empty.body')}
        action={canStart ? (
          <Button onClick={onStartVisit} data-testid="visits-empty-start">
            {t('record.actions.startVisit')}
          </Button>
        ) : null}
        data-testid="record-visits-empty"
      />
    );
  }

  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="record-visits">
        {rows.map((row) => (
          <Card
            key={row.id}
            role="button"
            onClick={() => onOpenVisit(row)}
            data-testid={`visit-card-${row.id}`}
            style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 5 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: 14 }}>
                {t('visit.number', { n: formatNumber(row.seq) })}
              </span>
              <Badge variant={row.status === 'open' ? 'warning' : 'outline'}>
                {t(encounterStatusKey(row.status))}
              </Badge>
              <span style={{ marginInlineStart: 'auto', fontSize: 11.5, color: CT.mute }}>
                {formatDate(row.startedAtMs, 'numeric')}
              </span>
            </div>
            {row.reason && <div style={{ fontSize: 13, color: C.textPrimary }}>{row.reason}</div>}
            <div style={{ fontSize: 11.5, color: CT.mute }}>
              {t('record.visits.columns.counts')}: {counts(row)}
            </div>
            {row.summary && (
              <div style={{ fontSize: 12.5, color: CT.mute }}>{firstLine(row.summary)}</div>
            )}
          </Card>
        ))}
      </div>
    );
  }

  const columns = [
    {
      key: 'seq',
      header: t('record.visits.columns.visit'),
      render: (row) => (
        <span style={{ fontWeight: 700 }}>{t('visit.number', { n: formatNumber(row.seq) })}</span>
      ),
    },
    {
      key: 'date',
      header: t('record.visits.columns.date'),
      render: (row) => formatDate(row.startedAtMs, 'numeric'),
    },
    {
      key: 'reason',
      header: t('record.visits.columns.reason'),
      render: (row) => row.reason || t('common.labels.none'),
    },
    {
      key: 'status',
      header: t('record.visits.columns.status'),
      render: (row) => (
        <Badge variant={row.status === 'open' ? 'warning' : 'outline'}>
          {t(encounterStatusKey(row.status))}
        </Badge>
      ),
    },
    {
      key: 'counts',
      header: t('record.visits.columns.counts'),
      align: 'center',
      // The server's counts. An encounter row carries no children by contract.
      render: counts,
    },
    {
      key: 'summary',
      header: t('record.visits.columns.summary'),
      render: (row) => firstLine(row.summary, 60) || t('common.labels.none'),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      onRowActivate={onOpenVisit}
      rowProps={(row) => ({
        'data-testid': `visit-row-${row.id}`,
        'aria-label': t('record.visits.openLabel', { n: formatNumber(row.seq) }),
      })}
      data-testid="record-visits"
    />
  );
}
